/**
 * AllowlistTable Component (AC-921)
 *
 * Displays the email allowlist as a table with:
 * - Email, Added By, Date, Registration Status columns
 * - is_registered shown as "Registered" or "Pending" badge
 * - Search input for filtering
 * - Remove button per row with ConfirmationDialog
 * - Empty state when no entries
 * - Loading state
 *
 * Epic: AC-915 - Soft-Launch Email Allowlist with Admin Portal
 */

import React, { useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { AllowlistEntry } from '../../types';
import ConfirmationDialog from '../common/ConfirmationDialog';

interface AllowlistTableProps {
  entries: AllowlistEntry[];
  total: number;
  isLoading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onRemove: (entryId: number) => void;
  onPageChange: (page: number) => void;
  currentPage: number;
}

const AllowlistTable: React.FC<AllowlistTableProps> = ({
  entries,
  total,
  isLoading,
  searchQuery,
  onSearchChange,
  onRemove,
  onPageChange,
  currentPage,
}) => {
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);

  const handleRemoveClick = (entryId: number) => {
    setConfirmRemoveId(entryId);
  };

  const handleConfirmRemove = () => {
    if (confirmRemoveId !== null) {
      onRemove(confirmRemoveId);
      setConfirmRemoveId(null);
    }
  };

  const handleCancelRemove = () => {
    setConfirmRemoveId(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading && entries.length === 0) {
    return (
      <div data-testid="allowlist-loading" aria-busy="true" className="animate-pulse space-y-3">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
      </div>
    );
  }

  const isDialogOpen = confirmRemoveId !== null;

  return (
    <div>
      {/* Main content - hidden from assistive tech when dialog is open */}
      <div aria-hidden={isDialogOpen || undefined}>
      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            role="textbox"
            aria-label="Search emails"
            placeholder="Search emails..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
      </div>

      {/* Table or empty state */}
      {entries.length === 0 ? (
        <div data-testid="allowlist-empty" className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No emails found in the allowlist.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Added By
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    {entry.email}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {entry.added_by_username || 'Unknown'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(entry.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {entry.is_registered ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        Registered
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <button
                      onClick={() => handleRemoveClick(entry.id)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                      aria-label="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Confirmation Dialog */}
      {confirmRemoveId !== null && (
        <ConfirmationDialog
          title="Remove Email"
          message="Are you sure you want to remove this email from the allowlist? This action cannot be undone."
          confirmText="Confirm"
          cancelText="Cancel"
          confirmVariant="danger"
          onConfirm={handleConfirmRemove}
          onCancel={handleCancelRemove}
        />
      )}
    </div>
  );
};

export default AllowlistTable;
